import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Filter, Users as UsersIcon, Mail, Phone } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { useUrlState } from '@/hooks/useUrlState';
import { adminApi } from '@/services/api';
import { formatNPRCompact, getInitials, formatDate } from '@/utils/format';
import { Avatar, AvatarFallback, Badge, Button, Card, CardContent, Input } from '@nepal-ecom/ui';

interface Customer {
  id: string;
  email: string | null;
  phone: string | null;
  fullName: string;
  isActive: boolean;
  isVerified: boolean;
  avatarUrl: string | null;
  loyaltyPoints: number;
  createdAt: string;
  orderCount: number;
  totalSpend: number;
}

function spendTier(spent: number) {
  if (spent >= 30_000) return { label: 'Top tier', cls: 'bg-emerald-100 text-emerald-800' };
  if (spent >= 10_000) return { label: 'Loyal', cls: 'bg-cobalt-100 text-cobalt-800' };
  if (spent > 0) return { label: 'New', cls: 'bg-amber-100 text-amber-800' };
  return { label: 'Window shopper', cls: 'bg-slate-100 text-slate-700' };
}

export function CustomerList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState({ search: '', status: 'all', page: '1' });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'customers', filters],
    queryFn: async () => {
      const res = await adminApi.getUsers({
        search: filters.search || undefined,
        status: filters.status === 'all' ? undefined : filters.status,
        page: filters.page,
        limit: 25,
      });
      return res.data.data as {
        data: Customer[];
        total: number;
        page: number;
        pageCount: number;
      };
    },
    placeholderData: (prev) => prev,
  });

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Customers"
        description={isLoading ? 'Loading…' : `${data?.total ?? 0} registered customers`}
      />

      <Card className="mb-5">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              value={filters.search}
              onChange={(e) => setFilters({ search: e.target.value, page: '1' })}
              placeholder="Search by name, email, phone…"
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ status: e.target.value, page: '1' })}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
            >
              <option value="all">All accounts</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <TableSkeleton rows={8} cols={6} />
      ) : isError ? (
        <Card>
          <CardContent className="p-10 text-center">
            <p className="text-sm text-red-600 mb-4">Failed to load customers.</p>
            <Button size="sm" onClick={() => refetch()}>Retry</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {['Customer', 'Contact', 'Orders', 'Spend', 'Tier', 'Joined', 'Status'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.data?.length === 0 && (
                  <tr><td colSpan={7}><EmptyState icon={UsersIcon} title="No customers" description="Try a different search." /></td></tr>
                )}
                {data?.data?.map((c) => {
                  const tier = spendTier(c.totalSpend);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => navigate(`/users/${c.id}`)}
                      className="hover:bg-primary-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback>{getInitials(c.fullName || c.email || '??')}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{c.fullName || '(no name)'}</p>
                            {c.isVerified && <p className="text-[10px] text-emerald-600 font-medium">Verified</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        {c.email && <p className="text-xs text-slate-600 flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</p>}
                        {c.phone && <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</p>}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-slate-700 tabular-nums">{c.orderCount}</td>
                      <td className="px-4 py-3.5 text-sm font-bold text-slate-900 tabular-nums">{formatNPRCompact(c.totalSpend)}</td>
                      <td className="px-4 py-3.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${tier.cls}`}>{tier.label}</span></td>
                      <td className="px-4 py-3.5 text-xs text-slate-500">{formatDate(c.createdAt)}</td>
                      <td className="px-4 py-3.5">
                        <Badge variant={c.isActive ? 'success' : 'destructive'}>
                          {c.isActive ? 'Active' : 'Suspended'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data && data.pageCount > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-600">
              <span>Page {data.page} of {data.pageCount}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={data.page <= 1} onClick={() => setFilters({ page: String(data.page - 1) })}>Previous</Button>
                <Button size="sm" variant="outline" disabled={data.page >= data.pageCount} onClick={() => setFilters({ page: String(data.page + 1) })}>Next</Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
